import React from "react";
import { useTranslation } from "react-i18next";

// Article Image Component
const ArticleImage = ({ image, imageAlt, title }) => {
  const handleImageError = (e) => {
    // Fallback to a placeholder image if the original fails to load
    e.target.src =
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=500&h=400&fit=crop";
    e.target.style.opacity = "0.7";
  };

  return (
    <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-700/30">
      <img
        src={image}
        alt={imageAlt || title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={handleImageError}
      />
    </div>
  );
};

// Category Badge Component
const CategoryBadge = ({ category }) => {
  const { i18n } = useTranslation("home");

  // Category translations
  const getCategoryTranslation = (cat) => {
    const currentLang = i18n.language;

    const translations = {
      "TadReamk Design": {
        "zh-CN": "TadReamk 设计",
        "zh-TW": "TadReamk 設計",
      },
      "TadReamk AI": {
        "zh-CN": "TadReamk 人工智能",
        "zh-TW": "TadReamk 人工智慧",
      },
      "IP Law": {
        "zh-CN": "知识产权法",
        "zh-TW": "智慧財產權法",
      },
      News: {
        "zh-CN": "新闻",
        "zh-TW": "新聞",
      },
      "TadReamk-AIGC": {
        en: "TadReamk AIGC",
        "zh-CN": "TadReamk AIGC",
        "zh-TW": "TadReamk AIGC",
      },
      "TadReamk-ERP": {
        en: "TadReamk ERP",
        "zh-CN": "TadReamk ERP",
        "zh-TW": "TadReamk ERP",
      },
    };

    return translations[cat]?.[currentLang] || cat;
  };

  return (
    <div className="inline-block px-3 py-1 bg-violet-600/20 text-violet-300 text-xs font-medium rounded-full mb-3">
      {getCategoryTranslation(category)}
    </div>
  );
};

// Article Title Component
const ArticleTitle = ({ title }) => {
  return (
    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-violet-300 transition-colors duration-300 line-clamp-2">
      {title}
    </h3>
  );
};

// Article Excerpt Component
const ArticleExcerpt = ({ excerpt }) => {
  return (
    <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3">
      {excerpt}
    </p>
  );
};

// Article Meta Component
const ArticleMeta = ({ author, date, readTime }) => {
  const { t } = useTranslation("home");
  return (
    <div className="flex items-center justify-between text-xs text-gray-400">
      <div className="flex items-center space-x-3">
        <span className="font-medium">{author}</span>
        <span>{date}</span>
      </div>
      <span>
        {readTime} {t("magazine.minRead")}
      </span>
    </div>
  );
};

// Article Card Component
export default function Article({
  image,
  title,
  excerpt,
  author,
  date,
  readTime,
  category,
  imageAlt = "",
  articleUrl = "#",
  onClick = null,
  article = null,
}) {
  const { i18n } = useTranslation("home");

  // Get translated content based on current language
  const getTranslatedContent = () => {
    // If no article object or no translations, use props as-is
    if (!article || !article.translations) {
      return { title, excerpt, author };
    }

    const currentLang = i18n.language;

    // Map frontend language codes to backend language codes
    const mapLanguageCode = (langCode) => {
      const mapping = {
        en: "en",
        "zh-CN": "zh", // Simplified Chinese
        "zh-TW": "zh-TW", // Traditional Chinese
      };
      return mapping[langCode] || langCode;
    };

    const backendLangCode = mapLanguageCode(currentLang);

    // If English or no translation exists for this language, use original
    if (backendLangCode === "en" || !article.translations[backendLangCode]) {
      return { title, excerpt, author };
    }

    // Get translation for current language
    const translation = article.translations[backendLangCode];

    return {
      title: translation.title || title,
      excerpt: translation.summary || excerpt,
      author: translation.author || author,
    };
  };

  const translatedContent = getTranslatedContent();

  const handleClick = () => {
    if (onClick && article) {
      onClick(article);
    } else {
      window.open(articleUrl, "_blank");
    }
  };

  return (
    <div
      className="bg-gray-800/30 backdrop-blur border border-gray-700/50 rounded-3xl p-6 hover:bg-gray-800/40 hover:border-violet-400/50 transition-all duration-300 group cursor-pointer"
      onClick={handleClick}
    >
      <ArticleImage
        image={image}
        imageAlt={imageAlt}
        title={translatedContent.title}
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <CategoryBadge category={category} />
      </div>

      <ArticleTitle title={translatedContent.title} />

      <ArticleExcerpt excerpt={translatedContent.excerpt} />

      <ArticleMeta
        author={translatedContent.author}
        date={date}
        readTime={readTime}
      />
    </div>
  );
}
